import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Activity, Footprints, Anchor, Wind, Shield, ShieldCheck, TrendingUp, TrendingDown, Minus, Sparkles, Clock, Bone, HeartPulse } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import BottomNav from "../components/BottomNav.jsx";
import useCountUp from "../lib/useCountUp.js";
import api from "../lib/api.js";

const PILLARS = [
  { key: "alignment", label: "Alignment", icon: Anchor,      accent: "#4ade80", italic: "supported posture" },
  { key: "mobility",  label: "Mobility",  icon: Wind,        accent: "#22d3ee", italic: "gait quality" },
  { key: "stability", label: "Stability", icon: Shield,      accent: "#a78bfa", italic: "balance & control" },
  { key: "endurance", label: "Endurance", icon: Activity,    accent: "#FBBF24", italic: "capacity over time" },
  { key: "recovery",  label: "Recovery",  icon: HeartPulse,  accent: "#ec4899", italic: "longevity & restoration" },
];

export default function Wellness() {
  const nav = useNavigate();
  const [today, setToday] = useState(null);
  const [life, setLife] = useState(null);
  const [week, setWeek] = useState(null);

  useEffect(() => {
    api.get("/wellness/today").then((r) => setToday(r.data)).catch(() => {});
    api.get("/wellness/lifetime").then((r) => setLife(r.data)).catch(() => {});
    api.get("/wellness/weekly").then((r) => setWeek(r.data)).catch(() => {});
  }, []);

  if (!today) {
    return <PhoneFrame><div className="p-6 text-slate-400 ps-serif ps-italic">Loading wellness…</div></PhoneFrame>;
  }

  return (
    <PhoneFrame>
      <div className="ps-aurora min-h-screen">
        <div className="px-5 pt-6 pb-28">
          <button onClick={() => nav(-1)} className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="wellness-back">
            <ChevronLeft size={16} /> Back
          </button>

          <div className="mt-5 ps-fade-up">
            <div className="ps-chip mb-3"><Sparkles size={11} /> Posture Wellness</div>
            <h1 className="ps-heading text-4xl font-extrabold leading-[1.05] tracking-tight">
              Walking, <span className="ps-serif ps-italic font-normal text-slate-500">re-interpreted.</span>
            </h1>
            <p className="text-slate-400 text-sm mt-2 ps-serif ps-italic max-w-[300px]">
              Apple Health × Back Transformer. Movement translated into spinal health.
            </p>
          </div>

          {/* Hero score */}
          <ScoreHero today={today} />

          {/* Insight */}
          <div className="mt-5 ps-card p-5 relative overflow-hidden">
            <div className="absolute -top-12 -right-10 w-44 h-44 rounded-full bg-[#4ade80]/15 blur-3xl pointer-events-none" />
            <div className="relative text-[10px] uppercase tracking-[0.25em] text-[#4ade80] font-bold">Today's insight</div>
            <div className="relative ps-serif text-[15px] leading-snug mt-2 text-slate-200" data-testid="wellness-insight">
              {today.insight.primary}
            </div>
            <div className="relative mt-4 grid grid-cols-2 gap-2">
              {today.insight.metrics.map((m) => (
                <div key={m.label} className="rounded-2xl border p-3" style={{ borderColor: `${m.accent}40`, background: `${m.accent}0d` }}>
                  <div className="text-[9px] uppercase tracking-widest font-bold" style={{ color: m.accent }}>{m.label}</div>
                  <div className="ps-heading text-lg font-extrabold ps-tabular mt-0.5">{m.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Posture Age */}
          <PostureAgeCard age={today.posture_age} />

          {/* 5 Pillars */}
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold mb-3">
              The five <span className="ps-serif ps-italic normal-case text-slate-500">pillars</span>
            </div>
            <div className="grid grid-cols-1 gap-3 ps-stagger">
              {PILLARS.map((p) => (
                <PillarRow key={p.key} pillar={p} value={today.pillars[p.key]} />
              ))}
            </div>
          </div>

          {/* Weekly recap */}
          {week && week.wins?.length > 0 && (
            <div className="mt-6 ps-card p-5 relative overflow-hidden">
              <div className="absolute -top-12 -left-10 w-44 h-44 rounded-full bg-[#a78bfa]/15 blur-3xl pointer-events-none" />
              <div className="relative text-[10px] uppercase tracking-[0.25em] text-[#a78bfa] font-bold">This week's posture wins</div>
              <div className="relative ps-heading text-2xl font-extrabold mt-1">
                7 <span className="ps-serif ps-italic font-normal text-slate-400">days, woven.</span>
              </div>
              <div className="relative mt-3 space-y-2">
                {week.wins.map((w) => (
                  <div key={w.label} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <DeltaIcon delta={w.delta} />
                      <span className="text-slate-300">{w.label}</span>
                    </div>
                    <div className="text-right">
                      <div className="ps-heading font-bold ps-tabular">{w.value}</div>
                      <div className="text-[10px] ps-tabular" style={{ color: w.delta > 0 ? "#4ade80" : w.delta < 0 ? "#ef4444" : "#94a3b8" }}>
                        {w.delta > 0 ? "+" : ""}{w.delta}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="relative mt-4 text-[12px] text-slate-400 ps-serif ps-italic leading-snug">
                {week.narrative}
              </div>
            </div>
          )}

          {/* Lifetime */}
          {life && <LifetimeCard life={life} />}
        </div>
      </div>
      <BottomNav />
    </PhoneFrame>
  );
}

function ScoreHero({ today }) {
  const score = useCountUp(today.wellness_score, { duration: 1800 });
  const delta = today.delta_today;
  return (
    <div className="mt-6 relative ps-fade-up">
      <div className="relative ps-glass-card p-6 overflow-hidden" style={{ boxShadow: `0 1px 0 0 rgba(255,255,255,0.08) inset, 0 -1px 0 0 rgba(0,0,0,0.25) inset, 0 28px 60px -20px rgba(74,222,128,0.45)` }}>
        <div aria-hidden className="ps-mesh-drift absolute inset-[-30%] opacity-50 pointer-events-none" style={{ background: "radial-gradient(circle at 25% 20%, #4ade8044 0%, transparent 45%), radial-gradient(circle at 80% 80%, #22d3ee25 0%, transparent 50%)", filter: "blur(20px)" }} />
        <div className="relative flex items-end justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#4ade80] font-bold">Posture Wellness Score™</div>
            <div className="ps-heading text-[5rem] font-extrabold leading-[0.9] mt-2 ps-tabular" data-testid="wellness-score">
              {score}
            </div>
            <div className="text-xs text-slate-400 mt-1">out of 100</div>
          </div>
          <DeltaPill delta={delta} />
        </div>
        <ScoreArc score={today.wellness_score} />
      </div>
    </div>
  );
}

function ScoreArc({ score }) {
  // Decorative gradient arc filling proportional to score
  const pct = Math.min(100, Math.max(0, score));
  return (
    <div className="relative mt-4 h-2 rounded-full bg-[#0E1F2A] overflow-hidden">
      <div
        className="h-full rounded-full"
        style={{
          width: `${pct}%`,
          background: "linear-gradient(90deg, #ef4444 0%, #FBBF24 35%, #4ade80 70%, #22d3ee 100%)",
          transition: "width 1.6s cubic-bezier(.2,.7,.2,1)",
          boxShadow: "0 0 14px rgba(74,222,128,0.7)",
        }}
      />
    </div>
  );
}

function DeltaPill({ delta }) {
  const Icon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const color = delta > 0 ? "#4ade80" : delta < 0 ? "#ef4444" : "#94a3b8";
  return (
    <div
      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold ps-tabular"
      style={{ background: `${color}1f`, color, border: `1px solid ${color}44` }}
    >
      <Icon size={12} />
      {delta > 0 ? "+" : ""}{delta} today
    </div>
  );
}

function PostureAgeCard({ age }) {
  const chrono = useCountUp(age.chronological_age, { duration: 1200 });
  const post = useCountUp(age.posture_age, { duration: 1600 });
  const diff = age.years_difference;
  return (
    <div className="mt-5 ps-glass-card p-5 relative overflow-hidden" style={{ boxShadow: `0 1px 0 0 rgba(255,255,255,0.08) inset, 0 -1px 0 0 rgba(0,0,0,0.25) inset, 0 28px 60px -22px rgba(167,139,250,0.4)` }}>
      <div aria-hidden className="ps-mesh-drift absolute inset-[-30%] opacity-50 pointer-events-none" style={{ background: "radial-gradient(circle at 20% 80%, #a78bfa44 0%, transparent 45%), radial-gradient(circle at 80% 20%, #4ade8025 0%, transparent 50%)", filter: "blur(20px)" }} />
      <div className="relative flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-[#a78bfa]">
        <Bone size={12} /> Posture Age™
      </div>
      <div className="relative mt-3 grid grid-cols-[1fr_auto_1fr] items-end gap-3">
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Chronological</div>
          <div className="ps-heading text-5xl font-extrabold ps-tabular mt-1 text-slate-300" data-testid="posture-age-chrono">{chrono}</div>
        </div>
        <div className="text-center pb-2">
          <div
            className="px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest"
            style={{ background: diff > 0 ? "#4ade8022" : "#94a3b822", color: diff > 0 ? "#4ade80" : "#94a3b8", border: `1px solid ${diff > 0 ? "#4ade8055" : "#94a3b855"}` }}
            data-testid="posture-age-diff"
          >
            {diff > 0 ? `−${diff} yrs` : diff < 0 ? `+${Math.abs(diff)} yrs` : "≈"}
          </div>
        </div>
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-widest text-[#a78bfa] font-bold">Posture</div>
          <div className="ps-heading text-5xl font-extrabold ps-tabular mt-1 text-[#a78bfa]" data-testid="posture-age-value">{post}</div>
        </div>
      </div>
      <div className="relative mt-4 text-[13px] ps-serif ps-italic text-slate-300 leading-snug text-center">
        {age.message}
      </div>
    </div>
  );
}

function PillarRow({ pillar, value }) {
  const Icon = pillar.icon;
  const v = useCountUp(value, { duration: 1200, decimals: 0 });
  return (
    <div className="ps-card p-4 relative overflow-hidden" data-testid={`pillar-${pillar.key}`}>
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-3xl pointer-events-none" style={{ background: `${pillar.accent}26` }} />
      <div className="relative flex items-center gap-3">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
          style={{
            background: `linear-gradient(160deg, ${pillar.accent}55 0%, ${pillar.accent}22 40%, ${pillar.accent}10 100%)`,
            border: `1px solid ${pillar.accent}66`,
            boxShadow: `inset 0 1.5px 0 rgba(255,255,255,0.32), inset 0 -3px 6px rgba(0,0,0,0.4), 0 4px 14px -4px ${pillar.accent}77`,
          }}
        >
          <Icon size={22} color={pillar.accent} strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: pillar.accent }}>
            {pillar.label} <span className="ps-serif ps-italic normal-case text-slate-500 ml-1">{pillar.italic}</span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-[#0E1F2A] overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${Math.min(100, value)}%`, background: `linear-gradient(90deg, ${pillar.accent}, ${pillar.accent}cc)`, boxShadow: `0 0 8px ${pillar.accent}88`, transition: "width 1.6s cubic-bezier(.2,.7,.2,1)" }} />
          </div>
        </div>
        <div className="ps-heading text-2xl font-extrabold ps-tabular" style={{ color: pillar.accent }} data-testid={`pillar-${pillar.key}-value`}>
          {v}
        </div>
      </div>
    </div>
  );
}

function LifetimeCard({ life }) {
  const miles = useCountUp(life.total_aligned_miles, { duration: 1800, decimals: 1 });
  const hours = useCountUp(life.total_posture_hours, { duration: 1800, decimals: 1 });
  const points = useCountUp(life.posture_points, { duration: 1800 });
  const saved = useCountUp(life.joint_savings.load_saved_lbs, { duration: 2200 });
  return (
    <div className="mt-6 ps-glass-card p-5 relative overflow-hidden" style={{ boxShadow: `0 1px 0 0 rgba(255,255,255,0.08) inset, 0 -1px 0 0 rgba(0,0,0,0.25) inset, 0 28px 60px -22px rgba(251,191,36,0.35)` }}>
      <div aria-hidden className="ps-mesh-drift absolute inset-[-30%] opacity-50 pointer-events-none" style={{ background: "radial-gradient(circle at 20% 20%, #FBBF2440 0%, transparent 45%), radial-gradient(circle at 80% 80%, #ec489925 0%, transparent 55%)", filter: "blur(20px)" }} />
      <div className="relative text-[10px] uppercase tracking-[0.25em] font-bold text-[#FBBF24]">Lifetime metrics</div>
      <div className="relative ps-heading text-2xl font-extrabold mt-1">
        Your <span className="ps-serif ps-italic font-normal text-slate-400">posture savings.</span>
      </div>
      <div className="relative mt-4 grid grid-cols-2 gap-3">
        <Stat label="Aligned miles" value={miles} accent="#4ade80" />
        <Stat label="Posture hours" value={hours} accent="#22d3ee" />
        <Stat label="Posture Points" value={points.toLocaleString()} accent="#FBBF24" />
        <Stat label="Joint load saved" value={`${saved.toLocaleString()} lbs`} accent="#ec4899" />
      </div>
      <div className="relative mt-4 space-y-2 text-[12px] ps-serif ps-italic text-slate-400 leading-snug">
        <div data-testid="spine-savings-msg">{life.spine_savings.message}</div>
        <div data-testid="joint-savings-msg">{life.joint_savings.message}</div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-2xl border p-3" style={{ borderColor: `${accent}40`, background: `${accent}0d` }}>
      <div className="text-[9px] uppercase tracking-widest font-bold" style={{ color: accent }}>{label}</div>
      <div className="ps-heading text-xl font-extrabold ps-tabular mt-0.5">{value}</div>
    </div>
  );
}

function DeltaIcon({ delta }) {
  if (delta > 0) return <TrendingUp size={14} color="#4ade80" />;
  if (delta < 0) return <TrendingDown size={14} color="#ef4444" />;
  return <Minus size={14} color="#94a3b8" />;
}
